import { AxiosResponse } from 'axios';

import { axiosAuthInstance } from 'api/base';
import { normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IGroupEditPayload, IPageType } from 'utils/types';

import { normalizeUser, normalizeUsersList } from './normalizers';
import { IUser, IUserData, IUserFormattedData, IUserResetOtpPayload } from './types';

const usersAPI = {
  async getAllUsers(filters: IAllPageFilters): Promise<IPageType<IUser>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosAuthInstance.get(`users`, {
      params: normalizedFilter,
    });

    return normalizePageData<IUserData, IUser>(
      response.data,
      normalizeUser,
      JSON.stringify(filters.search),
    );
  },

  async getUser(id: string): Promise<IUser> {
    const response = await axiosAuthInstance.get(`users/${id}`);

    return normalizeUser(response.data);
  },

  async createUser(user: IUserFormattedData): Promise<IUser> {
    const normalizedUser = prepareFormData(user);

    const response = await axiosAuthInstance.post('users', normalizedUser);

    return normalizeUser(response.data);
  },

  async updateUser({ id, login: _, ...user }: IUserFormattedData): Promise<IUser> {
    const normalizedUser = prepareFormData(user);

    const response = await axiosAuthInstance.put(`users/${id}`, normalizedUser);

    return normalizeUser(response.data);
  },

  async resetOtp({ id, ...payload }: IUserResetOtpPayload): Promise<AxiosResponse> {
    const normalizedUser = prepareFormData(payload);

    const response = await axiosAuthInstance.patch(`users/${id}`, normalizedUser);

    return response;
  },

  async deleteUser(id: string): Promise<AxiosResponse> {
    const response = await axiosAuthInstance.delete(`users/${id}`);

    return response.data;
  },

  async syncUsers(): Promise<AxiosResponse> {
    const response = await axiosAuthInstance.post(`users/sync`);

    return response.data;
  },

  async getUsersList(): Promise<{ label: string; value: number }[]> {
    const response = await axiosAuthInstance.get(`users/info`);

    return response?.data?.result?.map(normalizeUsersList) || [];
  },

  async groupEditUsers({ filters, settings }: IGroupEditPayload): Promise<AxiosResponse> {
    const { search } = normalizeSearchFiltersPayload(filters);

    const response = await axiosAuthInstance.patch('users', settings, {
      params: { search },
    });

    return response;
  },
};

export default usersAPI;
